import { createReceivingTracker, NO_STUDIES_RECEIVING, } from '@/lib/studies/receivingTracker';
import { useCallback, useEffect, useState } from 'react';
/**
 * How often the shared grace-window tick runs. One interval for the WHOLE
 * table, and only while at least one chip is showing — when nothing is
 * receiving there is no timer at all. Its only power is to let a mark age
 * out; `refresh` can never add one. Well under RECEIVING_GRACE_MS so a chip
 * clears promptly after the modality finishes, and it also covers the case
 * where list polling has paused (hidden tab) and no new snapshot is coming.
 */
const RECEIVING_TICK_MS = 15_000;
export function useReceivingStudyIds(studies) {
    // One tracker per table instance, created lazily and never replaced — it
    // carries the previous snapshot, so it must survive every re-render.
    const [tracker] = useState(() => createReceivingTracker());
    // Seeded with the tracker's own empty-result object so the first observe()
    // of a quiet page is an Object.is no-op rather than a mount re-render.
    const [receiving, setReceiving] = useState(NO_STUDIES_RECEIVING);
    useEffect(() => {
        // observe() is a FOLD over successive snapshots: it mutates the
        // previous-snapshot memory, so it has to run once per COMMIT, not once per
        // render attempt — which rules out deriving this during render. The
        // set-state-in-effect rule's usual cost (a cascading render) does not apply
        // here, because observe() returns a referentially stable set: this is an
        // Object.is no-op on every poll that changed nothing, whereas the
        // render-phase-update pattern the rule steers toward would cost an extra
        // render pass on EVERY poll instead of only on the ones that matter.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setReceiving(tracker.observe(studies, Date.now()));
    }, [tracker, studies]);
    const anyReceiving = receiving.size > 0;
    useEffect(() => {
        if (!anyReceiving)
            return;
        const handle = window.setInterval(() => {
            setReceiving(tracker.refresh(Date.now()));
        }, RECEIVING_TICK_MS);
        return () => window.clearInterval(handle);
    }, [tracker, anyReceiving]);
    const settle = useCallback((studyId) => setReceiving(tracker.settle(studyId, Date.now())), [tracker]);
    return { receiving, settle };
}
